import { memo } from "react";
import { FilePenLineIcon, FileSearchIcon, TerminalIcon, type LucideIcon } from "lucide-react";
import { type PendingApproval } from "../../session-logic";
import { cn } from "~/lib/utils";

interface ComposerPendingApprovalPanelProps {
  readonly approval: PendingApproval;
  readonly pendingCount: number;
}

/**
 * Composer-anchored "Forge is asking you" surface — the Claude Code / Codex
 * pattern where the input grows into an approval prompt you answer in place.
 * Styled as an intentional ask (calm primary/orange accent), not an amber
 * alarm: a request for permission is not an error. The action buttons render
 * beneath this via ComposerPendingApprovalActions.
 */
export const ComposerPendingApprovalPanel = memo(function ComposerPendingApprovalPanel({
  approval,
  pendingCount,
}: ComposerPendingApprovalPanelProps) {
  const Icon = approvalIconByKind[approval.requestKind];
  const title = approvalTitleByKind[approval.requestKind];
  const actionHint = approvalActionHintByKind[approval.requestKind];

  return (
    <div className="px-4 py-3.5 sm:px-5 sm:py-4">
      <div
        role="status"
        aria-live="polite"
        className={cn("rounded-lg border border-border bg-card px-3 py-2.5")}
      >
        <div className="flex items-center gap-2">
          <Icon className="size-4 shrink-0 text-warning-foreground" aria-hidden />
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{title}</span>
          {pendingCount > 1 ? (
            <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
              1/{pendingCount}
            </span>
          ) : null}
        </div>
        {approval.detail ? (
          <p className="mt-2 max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-md border border-border/50 bg-background/40 px-2 py-1 font-mono text-xs text-foreground/85">
            {approval.detail}
          </p>
        ) : null}
        <p className="mt-1.5 text-xs text-muted-foreground">{actionHint}</p>
      </div>
    </div>
  );
});

const approvalIconByKind: Record<PendingApproval["requestKind"], LucideIcon> = {
  command: TerminalIcon,
  "file-read": FileSearchIcon,
  "file-change": FilePenLineIcon,
};

const approvalTitleByKind: Record<PendingApproval["requestKind"], string> = {
  command: "Run a terminal command?",
  "file-read": "Read a file?",
  "file-change": "Save a file change?",
};

const approvalActionHintByKind: Record<PendingApproval["requestKind"], string> = {
  command: "Review the command, then approve or deny below.",
  "file-read": "Review the request, then approve or deny below.",
  "file-change": "Review the change, then approve or deny below.",
};
